import type { CredentialResponse } from "@react-oauth/google";

import * as React from "react";

import { storeToken } from "@/editor/reducers/utils/store-token";
import { handleTRPCClientError, trpc, trpcUtils } from "@/editor/utils/trpc";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "@replo/design-system/components/alert/Toast";
import { useForm } from "react-hook-form";
import { isEmpty } from "replo-utils/lib/misc";
import { passwordSchema } from "schemas/auth";
import { z } from "zod";

import useCurrentUser from "./useCurrentUser";
import { useLogAnalytics } from "./useLogAnalytics";

const nameValidationSchema = z.object({
  firstName: z.string().min(1, "You must enter a first name"),
  lastName: z.string(),
});

const passwordValidationSchema = z.object({
  password: z.string().min(1, "Please enter a valid password"),
  newPassword: passwordSchema,
});

export function useProfileForm({
  userGetQueryArgs = {
    shopifyInstallationParameters: {
      shopifyIntegrationId: undefined,
      pendingAppInstallationId: undefined,
    },
  },
}: { userGetQueryArgs?: Record<string, any> } = {}) {
  const logEvent = useLogAnalytics();
  const { user } = useCurrentUser();

  // Google connect error state
  const [googleErrorMessage, setGoogleErrorMessage] = React.useState<
    string | null
  >(null);

  const {
    register: registerName,
    handleSubmit: handleNameFormSubmit,
    formState: { errors: nameErrors, isDirty: hasNameChanged },
    setError: setNameError,
    reset: resetNameForm,
  } = useForm({
    defaultValues: {
      firstName: user?.firstName ?? "",
      lastName: user?.lastName ?? "",
    },
    resolver: zodResolver(nameValidationSchema),
  });

  const { mutate: updateProfileName, isPending: isUpdatingName } =
    trpc.user.updateProfileName.useMutation({
      onSuccess: (user) => {
        toast({
          header: "Profile Updated",
          message: "Your profile has been successfully updated",
        });
        logEvent("user.setting.updated", {
          infoUpdated: "name",
        });
        trpcUtils.user.get.setData(userGetQueryArgs, (oldData) => {
          if (!oldData) {
            return oldData;
          }

          return {
            ...oldData,
            firstName: user.firstName,
            lastName: user.lastName,
            name: `${user.firstName} ${user.lastName}`,
          };
        });
      },
      onError: (error) => {
        const zodFormErrorData = z
          .object({
            fieldErrors: z
              .object({
                firstName: z.array(z.string()).nullish(),
                lastName: z.array(z.string()).nullish(),
              })
              .nullish(),
            formErrors: z.array(z.string()).nullish(),
          })
          .safeParse(error.data);
        if (zodFormErrorData.success && !isEmpty(zodFormErrorData.data)) {
          if (zodFormErrorData.data?.fieldErrors?.firstName) {
            setNameError("firstName", {
              message: zodFormErrorData.data?.fieldErrors?.firstName[0],
            });
          }
          if (zodFormErrorData.data?.fieldErrors?.lastName) {
            setNameError("lastName", {
              message: zodFormErrorData.data?.fieldErrors?.lastName[0],
            });
          }
        } else {
          handleTRPCClientError(error);
        }
      },
      meta: {
        reploIgnoreDefaultErrorHandling: true,
      },
    });

  const {
    register: registerPassword,
    handleSubmit: handlePasswordFormSubmit,
    reset: resetPasswordForm,
    formState: { errors: passwordErrors, dirtyFields: dirtyPasswordFields },
  } = useForm({
    defaultValues: {
      password: "",
      newPassword: "",
    },
    resolver: zodResolver(passwordValidationSchema),
  });

  const { mutate: updateAccountPassword, isPending: isUpdatingPassword } =
    trpc.user.updateAccountPassword.useMutation({
      onSuccess: ({ token }) => {
        storeToken(token);
        toast({
          header: "Password Updated",
          message: "Your password has successfully been updated.",
        });
        logEvent("user.setting.updated", {
          infoUpdated: "password",
        });
        resetPasswordForm();
      },
    });

  // Google connect mutation
  const { mutate: connectGoogleAccount } =
    trpc.user.connectGoogleAccount.useMutation({
      onSuccess: ({ verifiedAt }) => {
        trpcUtils.user.get.setData(userGetQueryArgs, (oldData) => {
          if (!oldData) {
            return oldData;
          }

          return {
            ...oldData,
            verifiedAt,
          };
        });

        toast({
          header: "Google Account Connected",
          message: "Your Google account has been successfully connected",
        });
        setGoogleErrorMessage(null);
      },
      onError: (error) => setGoogleErrorMessage(error.message),
    });

  const handleGoogleConnect = async (
    credentialResponse: CredentialResponse,
  ) => {
    const { clientId, credential } = credentialResponse;
    if (clientId && credential) {
      connectGoogleAccount({ clientId, credential });
    }
  };

  const handleNameSubmit = async (
    data: z.infer<typeof nameValidationSchema>,
  ) => {
    updateProfileName(data);
    resetNameForm({}, { keepValues: true });
  };

  const handlePasswordSubmit = async (
    data: z.infer<typeof passwordValidationSchema>,
  ) => {
    updateAccountPassword(data);
  };

  const canUpdatePassword = Boolean(
    dirtyPasswordFields.password && dirtyPasswordFields.newPassword,
  );

  return {
    user,
    // Name form
    registerName,
    handleNameSubmit: (e: React.FormEvent) =>
      handleNameFormSubmit(handleNameSubmit)(e),
    nameErrors,
    isUpdatingName,
    hasNameChanged,
    // Password form
    registerPassword,
    handlePasswordSubmit: (e: React.FormEvent) =>
      handlePasswordFormSubmit(handlePasswordSubmit)(e),
    passwordErrors,
    isUpdatingPassword,
    canUpdatePassword,
    // Google connect
    googleErrorMessage,
    handleGoogleConnect,
  };
}
